/** @format */

import {
  CopyOutlined,
  FormOutlined,
  InfoCircleTwoTone,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Menu, Modal, Spin, Table, Tag, message } from "antd";
import { ColumnProps } from "antd/lib/table";
import Column from "antd/lib/table/Column";
import format from "date-fns/format";
import React, { useState } from "react";
import VoiceGainAuthSvcApi from "../../../apis/VoiceGainAuthSvcApi";
import {
  capitalizeWord,
  formatDateTime,
} from "../../../services/UtilityService";
import {
  AccountStatus,
  AccountType,
  BillingStyle,
  IAccountsList,
  IPhoneNumbers,
} from "../MainContent.types";
import { IAccountsListProps } from "./AccountsOverview.types";
import AccountsOverviewAddAccountModal from "./AccountsOverviewAddAccountModal";
import AccountsOverviewBillingModal from "./AccountsOverviewBillingModal";
import AccountsOverviewDeleteAccountModal from "./AccountsOverviewDeleteAccountModal";
import AccountsOverviewNotesModal from "./AccountsOverviewNotesModal";

interface IAccountsOverviewViewProps {
  accounts: IAccountsList[];
  // users: IUsers[];
  isLoading: boolean;
  isRecaching: boolean;
  sitekey: string;
  onSuccess: () => void;
  isDownloading: boolean;
  getColumnSearchProps: (
    data: string,
    type?: "user" | "account"
  ) => {
    filterDropdown: ColumnProps<IAccountsListProps>["filterDropdown"];
    filterIcon: ColumnProps<IAccountsListProps>["filterIcon"];
  };
  exportCsv: () => Promise<void>;
  onRefresh: () => void;
  onRecache: () => void;
}

const AccountsOverviewView: React.FC<IAccountsOverviewViewProps> = ({
  accounts,
  // users,
  isLoading,
  isRecaching,
  // isLoadingUsers,
  isDownloading,
  getColumnSearchProps,
  sitekey,
  onSuccess,
  exportCsv,
  onRefresh,
  onRecache,
}) => {
  const [openWindows, setOpenWindows] = useState<{
    [accountId: string]: Window;
  }>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleteModal, setDeleteModal] = useState(false); 
  const [notesModal, setNotesModal] = useState<null | {
    accountId: string;
    notes: string;
  }>(null);

  async function handleSuspendAccount(data: IAccountsList[]) {
    for (let dataEntry of data) {
      await VoiceGainAuthSvcApi.modAccount(dataEntry.accountId, {
        status: AccountStatus.Suspended,
      });
    }
    message.info(`${data.length} accounts have been suspended`);
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: IAccountsList) => ({
      disabled: record.deleted === true,
    }),
  };

  //Tab controller: if tab is already open, switch to that window, else if tab doesn't exist, open up a new window
  const newTabController = (accountId: string) => {
    const userUrl = new URL(
      `/account/${accountId}`,
      window.location.origin
    );
    if (openWindows[accountId] === undefined || openWindows[accountId].closed) {
      const newWindow = window.open(userUrl.toString(), accountId);
      if (newWindow !== null) {
        setOpenWindows({ ...openWindows, [accountId]: newWindow });
      }
    } else {
      openWindows[accountId].focus();
    }
  };

  const copyTextToClipboard = (text: string) => {
    const ele = document.createElement("input");
    document.body.appendChild(ele);
    ele.setAttribute("value", text);
    ele.select();
    document.execCommand("copy");
    document.body.removeChild(ele);
    message.info("Copied account ID to clipboard.");
  };

  // const findAccountUser = (creatorId: string) =>
  //   users.find((user) => user.userId === creatorId);

  const menu = (record: IAccountsList) => {
    return (
      <Menu>
        <Menu.Item key="1">
          <div
            onClick={() => newTabController(record.accountId)}
          >
            <InfoCircleTwoTone /> Details
          </div>
        </Menu.Item>
        <Menu.Item key="2">
          <div
            onClick={() => {
              setNotesModal({
                accountId: record.accountId,
                notes: record.notes,
              });
            }}
          >
            <FormOutlined style={{ marginRight: "0.2rem" }} /> Notes
          </div>
        </Menu.Item>
        <Menu.Item key="3">
          <AccountsOverviewDeleteAccountModal
            account={record.webName}
            accountId={record.accountId}
          />
        </Menu.Item>
      </Menu>
    );
  };

  return (
    <>
      {notesModal ? (
        <AccountsOverviewNotesModal
          accountId={notesModal.accountId}
          notes={notesModal.notes}
          isModalVisible={notesModal ? true : false}
          closeModal={() => {
            setNotesModal(null);
          }}
        />
      ) : null}
      <div>
        <Spin
          spinning={isLoading || isDownloading || isRecaching}
          tip={
            isDownloading
              ? "Downloading CSV..."
              : isRecaching
              ? "Recaching..."
              : "Fetching accounts data..."
          }
        >
          <div className="accounts-overview-page">
            <h2 style={{ textAlign: "center", margin: 0 }}>
              Accounts ({accounts.length}){" / "}Selected (
              {selectedRowKeys.length})
            </h2>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div>
                <AccountsOverviewAddAccountModal
                  sitekey={sitekey}
                  onSuccess={onSuccess}
                />
                <Button onClick={exportCsv} style={{ marginLeft: "0.4rem" }}>
                  Export to CSV
                </Button>
                <Button
                  disabled={!selectedRowKeys[0]}
                  onClick={() => setDeleteModal(true)}
                  style={{ marginLeft: "0.4rem" }}
                >
                  Suspend Selected Accounts
                </Button>
              </div>
              <div>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    onRecache();
                  }}
                  style={{ marginRight: "15px" }}
                >
                  Recache
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    onRefresh();
                  }}
                >
                  Refresh
                </Button>
              </div>
            </div>
            <Modal
              footer={[
                <Button
                  type="default"
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedRowKeys([]);
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  type="primary"
                  onClick={() => {
                    handleSuspendAccount(
                      accounts.filter((account) =>
                        selectedRowKeys.includes(
                          account.accountId + account.termsAcceptedBy
                        )
                      )
                    );
                  }}
                >
                  Suspend Accounts
                </Button>,
              ]}
              style={{ marginTop: "-75px" }}
              width={1000}
              title="Suspend Accounts"
              open={deleteModal}
              onCancel={() => {
                setDeleteModal(false);
              }}
            >
              <Table
                size="small"
                rowKey={(record: IAccountsList) =>
                  record.accountId + record.termsAcceptedBy
                }
                dataSource={accounts.filter((account) =>
                  selectedRowKeys.includes(
                    account.accountId + account.termsAcceptedBy
                  )
                )}
              >
                <Column
                  title="Name"
                  dataIndex="webName"
                  key="webName"
                  sorter={(a: { webName: string }, b: { webName: string }) =>
                    ("" + a.webName).localeCompare(b.webName)
                  }
                  ellipsis={true}
                  {...getColumnSearchProps("webName")}
                />
                <Column
                  width={140}
                  title="Email"
                  dataIndex="creatorEmail"
                  key="creatorEmail"
                  {...getColumnSearchProps("creatorEmail")}
                  render={(email) => <div>{email}</div>}
                />
                <Column
                  title="Created"
                  dataIndex="dateCreated"
                  key="dateCreated"
                  sorter={(
                    a: { dateCreated: string },
                    b: { dateCreated: string }
                  ) => ("" + a.dateCreated).localeCompare(b.dateCreated)}
                  render={(date) => {
                    if (date) {
                      return formatDateTime(date);
                    } else {
                      return formatDateTime("1971-01-01T00:00:00");
                    }
                  }}
                />
                <Column
                  title="Last Login"
                  dataIndex="lastLoginDate"
                  key="lastLoginDate"
                  filters={[
                    {
                      text: "Null",
                      value: "null",
                    },
                    {
                      text: "Not Null",
                      value: "notNull",
                    },
                  ]}
                  onFilter={(value, record) => {
                    if (value === "null") {
                      return (
                        record.lastLoginDate === "1971-01-01T00:00:00" ||
                        record.lastLoginDate === "0" ||
                        record.lastLoginDate === null
                      );
                    } else if (value === "notNull") {
                      return (
                        record.lastLoginDate !== "1971-01-01T00:00:00" &&
                        record.lastLoginDate !== "0" &&
                        record.lastLoginDate !== null
                      );
                    }
                    return true;
                  }}
                  sorter={(
                    a: { lastLoginDate: string },
                    b: { lastLoginDate: string }
                  ) => ("" + a.lastLoginDate).localeCompare(b.lastLoginDate)}
                  render={(date) => {
                    if (date) {
                      if (date === "1971-01-01T00:00:00") {
                        return "none";
                      } else {
                        return formatDateTime(date);
                      }
                    } else {
                      return "none";
                    }
                  }}
                />
                <Column
                  width={140}
                  title="Last Used"
                  dataIndex="lastUsedEpoch"
                  key="lastUsedEpoch"
                  filters={[
                    {
                      text: "Null",
                      value: "null",
                    },
                    {
                      text: "Not Null",
                      value: "notNull",
                    },
                  ]}
                  onFilter={(value: string | number | boolean, record: any) => {
                    if (value === "null") {
                      return record.lastUsedEpoch === 0;
                    }
                    if (value === "notNull") {
                      return record.lastUsedEpoch !== 0;
                    }
                    return false;
                  }}
                  sorter={(
                    a: { lastUsedEpoch: number },
                    b: { lastUsedEpoch: number }
                  ) => a.lastUsedEpoch - b.lastUsedEpoch}
                  render={(date) => {
                    if (date) {
                      const dateObject =
                        date.toString().length === 10
                          ? new Date(date * 1000)
                          : new Date(date);
                      return format(dateObject, "yyyy-MM-dd HH:mm:ss");
                    } else {
                      return "none";
                    }
                  }}
                />
                <Column<IAccountsList>
                  title={
                    <div
                      title="Type"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Type
                    </div>
                  }
                  width={145}
                  dataIndex="accountType"
                  key="accountType"
                  sorter={(
                    a: { accountType: string },
                    b: { accountType: string }
                  ) => ("" + a.accountType).localeCompare(b.accountType)}
                  onFilter={(
                    accountType: string | number | boolean,
                    record: IAccountsList
                  ) => {
                    if (typeof accountType === "string" && record.accountType) {
                      return record.accountType.indexOf(accountType) === 0;
                    }
                    return false;
                  }}
                  filters={[
                    {
                      text: AccountType.SpeechWorks,
                      value: AccountType.SpeechWorks,
                    },
                    { text: AccountType.Acp, value: AccountType.Acp },
                    {
                      text: AccountType.SpeechAnalytics,
                      value: AccountType.SpeechAnalytics,
                    },
                  ]}
                  render={(accountType, record) => {
                    const renderColor = () => {
                      switch (accountType) {
                        case AccountType.SpeechWorks:
                          return "orange";
                        case AccountType.SpeechAnalytics:
                          return "purple";
                        case AccountType.Acp:
                          return "blue";
                      }
                    };
                    return (
                      <div>
                        <div>
                          <Tag
                            style={{ width: "100%", textAlign: "center" }}
                            color={renderColor()}
                          >
                            {accountType}
                          </Tag>
                        </div>
                      </div>
                    );
                  }}
                />

                <Column
                  title="Do Not Suspend"
                  key="removeRow"
                  render={(record) => (
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        onClick={() => {
                          setSelectedRowKeys(
                            selectedRowKeys.filter(
                              (key) =>
                                key !==
                                record.accountId + record.termsAcceptedBy
                            )
                          );
                        }}
                      >
                        keep
                      </Button>
                    </div>
                  )}
                />
              </Table>
            </Modal>
            <Table
              rowSelection={rowSelection}
              dataSource={accounts}
              rowKey={(record: IAccountsList) =>
                record.accountId + record.termsAcceptedBy
              }
              className="accounts-overview-table"
              scroll={{ x: 1800 }}
              size="small"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "50", "100", "250"],
              }}
            >
              <Column
                width={140}
                title="Name"
                dataIndex="webName"
                key="webName"
                sorter={(a: { webName: string }, b: { webName: string }) =>
                  ("" + a.webName).localeCompare(b.webName)
                }
                ellipsis={true}
                {...getColumnSearchProps("webName")}
              />
              <Column
                title="ID"
                dataIndex="accountId"
                key="accountId"
                ellipsis={true}
                {...getColumnSearchProps("accountId")}
                render={(text) => (
                  <div
                    title={text}
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <CopyOutlined
                      onClick={() => copyTextToClipboard(text)}
                      style={{ marginRight: "0.2rem" }}
                    />
                    {text}
                  </div>
                )}
              />
              {/* <Column
              title="Email"
              dataIndex="creator"
              key="creatorEmail"
              {...getColumnSearchProps("email", "user")}
              render={(id) => {
                const user = findAccountUser(id);
                if (isLoadingUsers)
                  return (
                    <Skeleton active paragraph={{ rows: 1 }} title={false} />
                  );
                else return <div>{user && user.email}</div>;
              }}
            /> */}
              <Column
                width={140}
                title="Email"
                dataIndex="creatorEmail"
                key="creatorEmail"
                {...getColumnSearchProps("creatorEmail")}
                render={(email) => <div>{email}</div>}
              />
              <Column
                width={200}
                title="Created"
                dataIndex="dateCreated"
                key="dateCreated"
                sorter={(
                  a: { dateCreated: string },
                  b: { dateCreated: string }
                ) => ("" + a.dateCreated).localeCompare(b.dateCreated)}
                // render={(date) => formatDateTime(date)}
                render={(date) => {
                  if (date) {
                    return formatDateTime(date);
                  } else {
                    return formatDateTime("1971-01-01T00:00:00");
                  }
                }}
              />
              <Column
                width={120}
                title="Last Login"
                dataIndex="lastLoginDate"
                key="lastLoginDate"
                sorter={(
                  a: { lastLoginDate: string },
                  b: { lastLoginDate: string }
                ) => ("" + a.lastLoginDate).localeCompare(b.lastLoginDate)}
                filters={[
                  {
                    text: "Null",
                    value: "null",
                  },
                  {
                    text: "Not Null",
                    value: "notNull",
                  },
                ]}
                onFilter={(value, record) => {
                  if (value === "null") {
                    return (
                      record.lastLoginDate === "1971-01-01T00:00:00" ||
                      record.lastLoginDate === "0" ||
                      record.lastLoginDate === null
                    );
                  } else if (value === "notNull") {
                    return (
                      record.lastLoginDate !== "1971-01-01T00:00:00" &&
                      record.lastLoginDate !== "0" &&
                      record.lastLoginDate !== null
                    );
                  }
                  return true;
                }}
                render={(date) => {
                  if (date) {
                    if (date === "1971-01-01T00:00:00") {
                      return "none";
                    } else {
                      return formatDateTime(date);
                    }
                  } else {
                    return "none";
                  }
                }}
              />

              <Column
                width={120}
                title="Last Used"
                dataIndex="lastUsedEpoch"
                key="lastUsedEpoch"
                sorter={(
                  a: { lastUsedEpoch: number },
                  b: { lastUsedEpoch: number }
                ) => a.lastUsedEpoch - b.lastUsedEpoch}
                filters={[
                  {
                    text: "Null",
                    value: "null",
                  },
                  {
                    text: "Not Null",
                    value: "notNull",
                  },
                ]}
                onFilter={(value: string | number | boolean, record: any) => {
                  if (value === "null") {
                    return record.lastUsedEpoch === 0;
                  }
                  if (value === "notNull") {
                    return record.lastUsedEpoch !== 0;
                  }
                  return false;
                }}
                render={(date) => {
                  if (date) {
                    const dateObject =
                      date.toString().length === 10
                        ? new Date(date * 1000)
                        : new Date(date);
                    return format(dateObject, "yyyy-MM-dd HH:mm:ss");
                  } else {
                    return "none";
                  }
                }}
              />

              <Column<IAccountsList>
                title={
                  <div
                    title="Type"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Type
                  </div>
                }
                width={150}
                dataIndex="accountType"
                key="accountType"
                sorter={(
                  a: { accountType: string },
                  b: { accountType: string }
                ) => ("" + a.accountType).localeCompare(b.accountType)}
                onFilter={(
                  accountType: string | number | boolean,
                  record: IAccountsList
                ) => {
                  if (typeof accountType === "string" && record.accountType) {
                    return record.accountType.indexOf(accountType) === 0;
                  }
                  return false;
                }}
                filters={[
                  {
                    text: AccountType.SpeechWorks,
                    value: AccountType.SpeechWorks,
                  },
                  { text: AccountType.Acp, value: AccountType.Acp },
                  {
                    text: AccountType.SpeechAnalytics,
                    value: AccountType.SpeechAnalytics,
                  },
                ]}
                render={(accountType, record) => {
                  const renderColor = () => {
                    switch (accountType) {
                      case AccountType.SpeechWorks:
                        return "orange";
                      case AccountType.SpeechAnalytics:
                        return "purple";
                      case AccountType.Acp:
                        return "blue";
                    }
                  };
                  return (
                    <div>
                      <Tag
                        style={{ width: "100%", textAlign: "center" }}
                        color={renderColor()}
                      >
                        {accountType}
                      </Tag>
                    </div>
                  );
                }}
              />
              <Column
                width={110}
                title={
                  <div
                    title="Grafana"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Grafana
                  </div>
                }
                dataIndex="grafana"
                key="grafana"
                sorter={(
                  a: {
                    grafana?: {
                      orgName: string;
                    };
                  },
                  b: {
                    grafana?: {
                      orgName: string;
                    };
                  }
                ) =>
                  ("" + (a.grafana ? a.grafana.orgName : "")).localeCompare(
                    b.grafana ? b.grafana.orgName : ""
                  )
                }
                render={(grafana) => {
                  if (grafana && grafana.orgName) {
                    return <>{grafana.orgName}</>;
                  }
                }}
              />
              <Column
                width={140}
                title={
                  <div
                    title="Features"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Features
                  </div>
                }
                dataIndex="premiumFeatures"
                key="premiumFeatures"
                onFilter={(
                  feature: string | number | boolean,
                  record: IAccountsList
                ) => {
                  if (record.premiumFeatures) {
                    return record.premiumFeatures.includes(feature.toString());
                  }
                  return false;
                }}
                filters={[
                  { text: "Edge", value: "Edge" },
                  { text: "IVR", value: "IVR" },
                  { text: "API", value: "API" },
                  { text: "Websocket", value: "Websocket" },
                  { text: "Transcribe", value: "Transcribe" },
                  { text: "Analytics", value: "Analytics" },
                ]}
                render={(features, record: IAccountsList) => {
                  const highlightMain = (feature: string) => {
                    if (feature === record.mainMode) return "green";
                  };
                  if (features) {
                    return features.map((feature) => (
                      <span key={feature}>
                        <Tag
                          color={highlightMain(feature)}
                          style={{
                            width: "100%",
                            textAlign: "center",
                          }}
                        >
                          {feature}
                        </Tag>
                      </span>
                    ));
                  }
                }}
              />
              {/* <Column
              title={
                <div
                  title="Main Mode"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Main Mode
                </div>
              }
              dataIndex="mainMode"
              key="mainMode"
              onFilter={(feature: string, record: IAccountsList) => {
                if (record.mainMode) {
                  return record.mainMode.includes(feature);
                }
                return false;
              }}
              filters={[
                { text: "Edge", value: "Edge" },
                { text: "IVR", value: "IVR" },
                { text: "API", value: "API" },
                { text: "Websocket", value: "Websocket" },
                { text: "Transcribe", value: "Transcribe" },
                { text: "Analytics", value: "Analytics" },
              ]}
              render={(mode) => {
                const renderTagColor = (feature: string) => {
                  if (feature === "Edge") return "cyan";
                  else if (feature === "IVR") return "green";
                  else if (feature === "API") return "gold";
                  else if (feature === "Websocket") return "purple";
                  else if (feature === "Analytics") return "geekblue";
                  else if (feature === "Transcribe") return "lime";
                  else return "blue";
                };

                return mode ? (
                  <div>
                    <Tag
                      color={renderTagColor(mode)}
                      style={{ marginTop: "0.4rem" }}
                    >
                      {mode}
                    </Tag>
                  </div>
                ) : null;
              }}
            /> */}
              <Column<IAccountsList>
                width={140}
                title={
                  <div
                    title="Number of Contexts"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    # Contexts
                  </div>
                }
                dataIndex="numberCloudContexts"
                key="contexts"
                align="center"
                sorter={(
                  a: { numberCloudContexts: number },
                  b: { numberCloudContexts: number }
                ) => a.numberCloudContexts - b.numberCloudContexts}
                render={(text) => {
                  if (text) {
                    return <Tag style={{ width: "40px" }}>{text}</Tag>;
                  } else return <Tag style={{ width: "40px" }}>0</Tag>;
                }}
                filters={[
                  { text: "1", value: "1" },
                  { text: "2+", value: "2" },
                  { text: "5+", value: "5" },
                  { text: "10+", value: "10" },
                ]}
                onFilter={(value, record) => {
                  switch (value) {
                    case "1":
                      return record.numberCloudContexts === 1;
                    case "2":
                      return record.numberCloudContexts >= 2;
                    case "5":
                      return record.numberCloudContexts >= 5;
                    case "10":
                      return record.numberCloudContexts >= 10;
                    default:
                      return true;
                  }
                }}
              />

              <Column<IAccountsList>
                width={140}
                title={
                  <div
                    title="Number of Edge Deployments"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    # Edge
                  </div>
                }
                dataIndex="numberEdgeDeployments"
                key="edgeDeployments"
                align="center"
                sorter={(
                  a: { numberEdgeDeployments: number },
                  b: { numberEdgeDeployments: number }
                ) => a.numberEdgeDeployments - b.numberEdgeDeployments}
                render={(text) => {
                  if (text) {
                    if (parseInt(text) < 5)
                      return (
                        <Tag style={{ width: "40px" }} color="green">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 5 && parseInt(text) < 10)
                      return (
                        <Tag style={{ width: "40px" }} color="blue">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 10 && parseInt(text) < 15)
                      return (
                        <Tag style={{ width: "40px" }} color="purple">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 15 && parseInt(text) < 20)
                      return (
                        <Tag style={{ width: "40px" }} color="volcano">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 20)
                      return (
                        <Tag style={{ width: "40px" }} color="magenta">
                          {text}
                        </Tag>
                      );
                  } else return <Tag style={{ width: "40px" }}>0</Tag>;
                }}
                filters={[
                  { text: "1+", value: "1" },
                  { text: "2+", value: "2" },
                  { text: "3+", value: "3" },
                  { text: "4+", value: "4" },
                ]}
                onFilter={(value, record) => {
                  switch (value) {
                    case "1":
                      return record.numberEdgeDeployments >= 1;
                    case "2":
                      return record.numberEdgeDeployments >= 2;
                    case "3":
                      return record.numberEdgeDeployments >= 3;
                    case "4":
                      return record.numberEdgeDeployments >= 4;
                    default:
                      return true;
                  }
                }}
              />

              <Column<IAccountsList>
                width={150}
                title={
                  <div
                    title="Number of Websockets"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    # Websockets
                  </div>
                }
                dataIndex="numberWebsockets"
                key="webSockets"
                align="center"
                sorter={(
                  a: { numberWebsockets: number },
                  b: { numberWebsockets: number }
                ) => a.numberWebsockets - b.numberWebsockets}
                render={(text) => {
                  if (text) {
                    if (parseInt(text) < 5)
                      return (
                        <Tag style={{ width: "40px" }} color="green">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 5 && parseInt(text) < 10)
                      return (
                        <Tag style={{ width: "40px" }} color="blue">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 10 && parseInt(text) < 15)
                      return (
                        <Tag style={{ width: "40px" }} color="purple">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 15 && parseInt(text) < 20)
                      return (
                        <Tag style={{ width: "40px" }} color="volcano">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 20)
                      return (
                        <Tag style={{ width: "40px" }} color="magenta">
                          {text}
                        </Tag>
                      );
                  } else return <Tag style={{ width: "40px" }}>0</Tag>;
                }}
                filters={[
                  { text: "1+", value: "1" },
                  { text: "2+", value: "2" },
                  { text: "3+", value: "3" },
                  { text: "4+", value: "4" },
                ]}
                onFilter={(value, record) => {
                  switch (value) {
                    case "1":
                      return record.numberWebsockets >= 1;
                    case "2":
                      return record.numberWebsockets >= 2;
                    case "3":
                      return record.numberWebsockets >= 3;
                    case "4":
                      return record.numberWebsockets >= 4;
                    default:
                      return true;
                  }
                }}
              />

              <Column<IAccountsList>
                width={130}
                title={
                  <div
                    title="Number of Phone Numbers"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    # Phone no.
                  </div>
                }
                dataIndex="phoneNumbers"
                key="phoneNumbers"
                align="center"
                sorter={(
                  a: { phoneNumbers: IPhoneNumbers[] },
                  b: { phoneNumbers: IPhoneNumbers[] }
                ) => {
                  let aLength = a.phoneNumbers ? a.phoneNumbers.length : 0;
                  let bLength = b.phoneNumbers ? b.phoneNumbers.length : 0;
                  return aLength - bLength;
                }}
                render={(phoneNumbers) => {
                  if (phoneNumbers) {
                    if (parseInt(phoneNumbers.length) < 5)
                      return (
                        <Tag style={{ width: "40px" }} color="green">
                          {phoneNumbers.length}
                        </Tag>
                      );
                    else if (
                      parseInt(phoneNumbers.length) >= 5 &&
                      parseInt(phoneNumbers.length) < 10
                    )
                      return (
                        <Tag style={{ width: "40px" }} color="blue">
                          {phoneNumbers.length}
                        </Tag>
                      );
                    else if (
                      parseInt(phoneNumbers.length) >= 10 &&
                      parseInt(phoneNumbers.length) < 15
                    )
                      return (
                        <Tag style={{ width: "40px" }} color="purple">
                          {phoneNumbers.length}
                        </Tag>
                      );
                    else if (
                      parseInt(phoneNumbers.length) >= 15 &&
                      parseInt(phoneNumbers.length) < 20
                    )
                      return (
                        <Tag style={{ width: "40px" }} color="volcano">
                          {phoneNumbers.length}
                        </Tag>
                      );
                    else if (parseInt(phoneNumbers.length) >= 20)
                      return (
                        <Tag style={{ width: "40px" }} color="magenta">
                          {phoneNumbers.length}
                        </Tag>
                      );
                  } else return <Tag style={{ width: "40px" }}>0</Tag>;
                }}
                filters={[
                  { text: "1+", value: "1" },
                  { text: "2+", value: "2" },
                  { text: "3+", value: "3" },
                  { text: "4+", value: "4" },
                ]}
                onFilter={(value, record) => {
                  switch (value) {
                    case "1":
                      return record.phoneNumbers?.length >= 1;
                    case "2":
                      return record.phoneNumbers?.length >= 2;
                    case "3":
                      return record.phoneNumbers?.length >= 3;
                    case "4":
                      return record.phoneNumbers?.length >= 4;
                    default:
                      return true;
                  }
                }}
              />

              <Column<IAccountsList>
                width={140}
                title={
                  <div
                    title="Number of Users"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    # Users
                  </div>
                }
                dataIndex="numberUsers"
                key="numberOfUsers"
                align="center"
                sorter={(
                  a: { numberUsers: number },
                  b: { numberUsers: number }
                ) => a.numberUsers - b.numberUsers}
                render={(text) => {
                  if (text) {
                    if (parseInt(text) < 5)
                      return (
                        <Tag style={{ width: "40px" }} color="green">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 5 && parseInt(text) < 10)
                      return (
                        <Tag style={{ width: "40px" }} color="blue">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 10 && parseInt(text) < 15)
                      return (
                        <Tag style={{ width: "40px" }} color="purple">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 15 && parseInt(text) < 20)
                      return (
                        <Tag style={{ width: "40px" }} color="volcano">
                          {text}
                        </Tag>
                      );
                    else if (parseInt(text) >= 20)
                      return (
                        <Tag style={{ width: "40px" }} color="magenta">
                          {text}
                        </Tag>
                      );
                  } else return <Tag style={{ width: "40px" }}>0</Tag>;
                }}
                filters={[
                  { text: "1", value: "1" },
                  { text: "2+", value: "2" },
                  { text: "5+", value: "5" },
                  { text: "10+", value: "10" },
                ]}
                onFilter={(value, record) => {
                  switch (value) {
                    case "1":
                      return record.numberUsers === 1;
                    case "2":
                      return record.numberUsers >= 2;
                    case "5":
                      return record.numberUsers >= 5;
                    case "10":
                      return record.numberUsers >= 10;
                    default:
                      return true;
                  }
                }}
              />

              <Column
                width={140}
                title={
                  <div
                    title="Balance"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Balance
                  </div>
                }
                dataIndex="billingAccountId"
                key="currentBalance"
                align="center"
                render={(text, record: IAccountsList) => {
                  return (
                    <AccountsOverviewBillingModal
                      billingId={text}
                      billingStyle={record.billingStyle}
                      lastRefill={record.lastRefill}
                      currentBalance={record.currentBalance}
                    />
                  );
                }}
                sorter={(a: IAccountsList, b: IAccountsList) => {
                  // Compare current balances
                  return (a.currentBalance || 0) - (b.currentBalance || 0);
                }}
                onFilter={(
                  currentBalance: string | number | boolean,
                  record: IAccountsList
                ) => {
                  if (record.currentBalance) {
                    const sign = () => {
                      if (
                        Math.sign(record.currentBalance) === 1 ||
                        Math.sign(record.currentBalance) === 0
                      ) {
                        return "positive";
                      } else if (Math.sign(record.currentBalance) === -1)
                        return "negative";
                    };
                    return sign() === currentBalance;
                  }
                  return false;
                }}
                filters={[
                  { text: "Negative", value: "negative" },
                  { text: "Positive", value: "positive" },
                ]}
              />

              <Column<IAccountsList>
                title={
                  <div
                    title="Billing"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Billing
                  </div>
                }
                width={145}
                dataIndex="billingStyle"
                key="billingStyle"
                sorter={(
                  a: { billingStyle: string },
                  b: { billingStyle: string }
                ) => ("" + a.billingStyle).localeCompare(b.billingStyle)}
                onFilter={(
                  billing: string | number | boolean,
                  record: IAccountsList
                ) => {
                  if (record.billingStyle) {
                    return record.billingStyle.includes(billing.toString());
                  }
                  return false;
                }}
                filters={[
                  {
                    text: BillingStyle.AutoRefill,
                    value: BillingStyle.AutoRefill,
                  },
                  {
                    text: BillingStyle.ManualRefill,
                    value: BillingStyle.ManualRefill,
                  },
                  { text: BillingStyle.Invoice, value: BillingStyle.Invoice },
                ]}
                render={(billingStyle, record) => {
                  return (
                    <div>
                      <div>
                        <Tag style={{ width: "100%", textAlign: "center" }}>
                          {billingStyle}
                        </Tag>
                      </div>
                    </div>
                  );
                }}
              />
              <Column
                width={140}
                title="Status"
                dataIndex="status"
                key="status"
                align="center"
                onFilter={(
                  status: AccountStatus | number | string | boolean,
                  record: IAccountsList
                ) => {
                  if (record.status) {
                    return record.status.indexOf(status.toString()) === 0;
                  }
                  return false;
                }}
                filters={[
                  { text: "Active", value: AccountStatus.Active },
                  { text: "Pending", value: AccountStatus.Pending },
                  { text: "Suspended", value: AccountStatus.Suspended },
                ]}
                render={(status, record) => {
                  const renderColor = () => {
                    switch (status) {
                      case AccountStatus.Active:
                        return "green";
                      case AccountStatus.Pending:
                        return "gold";
                      case AccountStatus.Suspended:
                        return "red";
                      default:
                        return "green";
                    }
                  };
                  return (
                    <Tag color={renderColor()} style={{ width: "100%" }}>
                      {status
                        ? capitalizeWord(status)
                        : capitalizeWord(AccountStatus.Active)}
                    </Tag>
                  );
                }}
              />
              <Column
                width={140}
                title="PCI-DSS"
                dataIndex="pciDss"
                key="pciDss"
                align="center"
                filters={[
                  { text: "True", value: true },
                  { text: "False", value: false },
                ]}
                onFilter={(value, record: IAccountsList) =>
                  record.pciDss == value
                }
                render={(pciDss) => (
                  <Tag color={pciDss == true ? "green" : "red"} key={pciDss}>
                    {pciDss.toString()}
                  </Tag>
                )}
              />
              {/* <Column
              title={
                <div
                  title="Referrer"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Referrer
                </div>
              }
              dataIndex="referrer"
              key="referrer"
              render={(text) => {
                if (text) {
                  return text.charAt(0).toUpperCase() + text.slice(1);
                }
              }}
              onFilter={(referrer: string, record: IAccountsList) => {
                if (record.referrer) {
                  return record.referrer.indexOf(referrer) === 0;
                }
                return false;
              }}
              filters={[
                { text: "Google", value: "google" },
                { text: "Twitter", value: "twitter" },
                { text: "Facebook", value: "facebook" },
                { text: "Reddit", value: "reddit" },
                { text: "Quora", value: "quora" },
                { text: "Fiverr", value: "fiverr" },
                { text: "Direct", value: "direct" },
                { text: "Other", value: "other" },
              ]}
            /> */}
              {/* <Column
              title={
                <div
                  title="Last Login"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Last Login
                </div>
              }
              dataIndex="lastLoginDate"
              key="lastLogin"
              sorter={(
                a: { lastLoginDate: string },
                b: { lastLoginDate: string }
              ) => {
                a.lastLoginDate = a.lastLoginDate || "";
                b.lastLoginDate = b.lastLoginDate || "";
                return a.lastLoginDate.localeCompare(b.lastLoginDate);
              }}
              render={(text: string, record) => {
                if (text) {
                  return (
                    <div>
                      <div style={{ fontSize: "0.8rem" }}>
                        {formatDateTime(text)}
                      </div>
                    </div>
                  );
                }
              }}
            />
            <Column
              title={
                <div
                  title="Referral"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Referral
                </div>
              }
              dataIndex="referral"
              key="referral"
              align="center"
              sorter={(a: { referral }, b: { referral }) => {
                const aRef = a.referral ? a.referral : "";
                const bRef = b.referral ? b.referral : "";
                return Object.keys(bRef).length - Object.keys(aRef).length;
              }}
              render={(text) => {
                return <AccountsOverviewReferralModal referral={text} />;
              }}
            /> */}
              <Column
                width={110}
                align="center"
                title="Deleted"
                dataIndex="deleted"
                key="deleted"
                sorter={(a: IAccountsList, b: IAccountsList) => {
                  return a.deleted === b.deleted ? 0 : a.deleted ? 1 : -1;
                }}
                render={(deleted) => {
                  return <Tag color={deleted ? "red" : "green"}>{deleted ? "Yes" : "No"}</Tag>;
                }}
                filters={[
                  { text: "True", value: true },
                  { text: "False", value: false },
                ]}
                onFilter={(value, record: IAccountsList) =>
                  record.deleted === value
                }
              />
              <Column
                width={110}
                align="center"
                title="More"
                dataIndex="accountId"
                key="more"
                render={(recording, record: IAccountsList) => {
                  const items: MenuProps["items"] = [
                    {
                      key: "1",
                      label: (
                        <div
                          onClick={() =>
                            newTabController(record.accountId)
                          }
                        >
                          <InfoCircleTwoTone /> Details
                        </div>
                      ),
                    },

                    {
                      key: "2",
                      label: (
                        <div
                          onClick={() => {
                            setNotesModal({
                              accountId: record.accountId,
                              notes: record.notes,
                            });
                          }}
                        >
                          <FormOutlined style={{ marginRight: "0.2rem" }} />{" "}
                          Notes
                        </div>
                      ),
                    },
                    {
                      key: "3",
                      label: (
                        <AccountsOverviewDeleteAccountModal
                          account={record.webName}
                          accountId={record.accountId}
                        />
                      ),
                    },
                  ];
                  return (
                    <Dropdown menu={{ items }} disabled={record.deleted}>
                      <Button>
                        <MoreOutlined />
                      </Button>
                    </Dropdown>
                  );
                }}
              />
            </Table>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default AccountsOverviewView;
